import { chunk, range } from 'lodash';
import type { RiskInsightsSiteRow } from 'src/app/portfolio/state/api/riskInsightsSitesQuery';
import { buildSiteListCsv } from 'src/app/portfolio/utils/buildSiteListCsv';

export type FetchPageResult = { items: RiskInsightsSiteRow[]; total: number };
export type FetchPage = (page: number, pageSize: number) => Promise<FetchPageResult>;
export type DownloadFile = (content: string, filename: string) => Promise<void>;
export type DownloadProgress = { done: number; total: number };
export type OnProgress = (progress: DownloadProgress | null) => void;

export type RunSiteListDownloadParams = {
    fetchPage: FetchPage;
    downloadFile: DownloadFile;
    onProgress: OnProgress;
    pageSize: number;
    concurrency: number;
    progressThreshold: number;
    filename: string;
};

/**
 * Orchestrates a full-portfolio CSV download:
 *  1. Probes page 1 to learn the total.
 *  2. Fetches remaining pages in batches of `concurrency` via `Promise.all`.
 *  3. Builds the CSV and hands it to `downloadFile`.
 *
 * `onProgress` fires only when the total reaches `progressThreshold` —
 * smaller portfolios complete fast enough that callers can show a spinner
 * without per-page updates.
 */
export const runSiteListDownload = async ({
    fetchPage,
    downloadFile,
    onProgress,
    pageSize,
    concurrency,
    progressThreshold,
    filename,
}: RunSiteListDownloadParams): Promise<void> => {
    const first = await fetchPage(1, pageSize);
    const totalPages = first.total === 0 ? 0 : Math.ceil(first.total / pageSize);
    const shouldTrackProgress = first.total >= progressThreshold;

    if (shouldTrackProgress) {
        onProgress({ done: 1, total: totalPages });
    }

    const remainingPages = totalPages > 1 ? range(2, totalPages + 1) : [];
    const batches = chunk(remainingPages, concurrency);

    const remainingItems: RiskInsightsSiteRow[] = [];
    let completed = 1;

    for (const batch of batches) {
        const batchResults = await Promise.all(batch.map(page => fetchPage(page, pageSize)));
        for (const result of batchResults) {
            remainingItems.push(...result.items);
            completed += 1;
            if (shouldTrackProgress) {
                onProgress({ done: completed, total: totalPages });
            }
        }
    }

    await downloadFile(buildSiteListCsv([...first.items, ...remainingItems]), filename);
};
